import * as Sentry from '@sentry/react';
import {
  invalidCredentialsMessage,
  somethingWentWrongMessage,
} from '../constants/constants';
import { isSbConnectUser } from '../helpers/helper';
import { parseJwt } from '../helpers/parseJwt';
import { ILoginResponse } from '../interfaces/authInterface';
import { IPrevNotes } from '../interfaces/noteInterface';
import { withAuth } from './client';

const { VITE_DEV_ENDPOINT_URL, VITE_DEV_AUTH_ENDPOINT_URL } = import.meta.env;

// const baseUrl = DEV ? '/api' : VITE_DEV_ENDPOINT_URL;
const baseUrl = `${VITE_DEV_ENDPOINT_URL}/v1/sb_note/audio_to_draft`;

export const createAINote = (
  audioBlob: Blob,
  exam_time: string,
  token: string,
  patientId?: string
) => {
  return Sentry.startSpan(
    {
      op: 'http.client',
      name: 'POST /v1/sb_note/audio_to_draft',
    },
    async span => {
      console.log('tu je');
      const formData = new FormData();
      const timestamp = new Date().toISOString().replace(/[-:.]/g, '');
      const filename = `audio_${timestamp}.wav`;
      console.log('tu je 2');
      const { base_id } = parseJwt(token);
      console.log('tu je 3');

      const isSbConnect = isSbConnectUser(token);
      console.log('tu je 4');

      const postData: Record<string, string> = {
        baseid: base_id,
        exam_time: exam_time,
        ...(patientId ? { patientid: patientId } : {}),
        ...(isSbConnect && patientId ? { visitpush: 'true' } : {}),
      };
      console.log(isSbConnect, 'isSbConnect');
      console.log(postData, 'postData');
      console.log(audioBlob.size, 'audioBlob.size');
      console.log(audioBlob.type, 'audioBlob.type');

      // Add metadata to span
      span.setAttribute('audio.size', audioBlob.size);
      span.setAttribute('audio.type', audioBlob.type);
      span.setAttribute('exam_time', exam_time);
      if (patientId) {
        span.setAttribute('has_patient_id', true);
      }

      try {
        for (const k in postData) {
          formData.append(k, String(postData[k as keyof typeof postData]));
        }
        formData.append('audio', audioBlob, filename);
      } catch (e) {
        Sentry.captureException(e, {
          level: 'error',
          tags: { operation: 'formdata_creation' },
        });
        throw e;
      }

      const requestOptions = withAuth(
        { method: 'POST', body: formData },
        token
      );

      console.log('FormData entries:', Array.from(formData.entries()));

      try {
        const response = await fetch(baseUrl, requestOptions);
        if (response.status === 200) {
          const data = await response.json();
          span.setAttribute('caseid', data.caseid);
          return data.caseid;
        } else {
          span.setStatus({ code: 2, message: `HTTP ${response.status}` });
          throw response.status;
        }
      } catch (error) {
        Sentry.captureException(error, {
          level: 'error',
          tags: { operation: 'create_ai_note' },
          contexts: {
            request: {
              exam_time,
              has_patient_id: !!patientId,
              audio_size: audioBlob.size,
            },
          },
        });
        return false;
      }
    }
  );
};

const isAbortError = (e: unknown): boolean => {
  if (typeof e !== 'object' || e === null) return false;
  const maybe = e as { name?: unknown };
  return typeof maybe.name === 'string' && maybe.name === 'AbortError';
};

export const getAINote = async (
  id: string,
  token: string,
  signal?: AbortSignal | null
) => {
  // Note: No tracing here - this function is called every 5 seconds during polling
  // which would create excessive trace volume (12 traces/min per pending note)
  const { base_id } = parseJwt(token);
  const url = `${baseUrl}?caseid=${id}&baseid=${base_id}`;

  const requestOptions: RequestInit = withAuth(
    { method: 'GET' },
    token,
    signal ?? null
  );

  try {
    const response = await fetch(url, requestOptions);

    if (response.status === 200) {
      const data = await response.json();
      const result = (data && data.result) || {};
      const contentKeys = [
        'HistoryOfPresentIllnessText',
        'ReviewOfSystemsText',
        'PhysicalExamText',
        'AssessmentText',
        'PlanOfCareText',
        'MedicationsText',
        'SocialHistoryText',
        'Meta',
        'Header',
        'Template',
        'Cleanup',
      ];
      const hasResult = contentKeys.some(
        k => k in result || k.toLowerCase() in result
      );

      const red_flag = data?.red_flag ?? result?.red_flag ?? false;

      return {
        caseid: id,
        created_on: new Date().toISOString(),
        hasResult,
        red_flag,
        ...result,
      };
    } else if (response.status === 204) {
      return { result: { caseid: id } };
    } else {
      throw response.status;
    }
  } catch (e: unknown) {
    if (isAbortError(e)) {
      throw e;
    }

    // Only capture non-abort errors
    Sentry.captureException(e, {
      level: 'error',
      tags: { operation: 'get_ai_note' },
      contexts: {
        request: { caseid: id },
      },
    });

    return { error: somethingWentWrongMessage };
  }
};

export const login = async (
  username: string,
  password: string
): Promise<ILoginResponse> => {
  return Sentry.startSpan(
    {
      op: 'http.client',
      name: 'POST /api/v1/user/sign/in',
    },
    async span => {
      const url = `${VITE_DEV_AUTH_ENDPOINT_URL || 'https://authdev.scribebrain.com'}/api/v1/user/sign/in`;
      const postData = {
        username,
        password,
      };

      span.setAttribute('username', username);

      const headers = new Headers();
      headers.append('Content-Type', 'application/json');
      const requestOptions = {
        method: 'POST',
        headers: headers,
        body: JSON.stringify(postData),
      };

      try {
        const resp = await fetch(url, requestOptions).then(response =>
          response.json()
        );

        if (!resp.error) {
          span.setAttribute('login_success', true);
          // Set user context for future errors
          Sentry.setUser({ username });
          return { token: resp.tokens.access, error: '' };
        } else {
          span.setAttribute('login_success', false);
          span.setAttribute('error_message', resp.msg);
          throw resp.msg;
        }
      } catch (msg) {
        const errorMessage =
          typeof msg === 'string' &&
          (msg.includes('wrong user') ||
            msg.includes('user with the given email') ||
            msg.includes('user with the given username'))
            ? invalidCredentialsMessage
            : somethingWentWrongMessage;

        // Only capture unexpected errors (not invalid credentials)
        if (errorMessage === somethingWentWrongMessage) {
          Sentry.captureException(msg, {
            level: 'error',
            tags: { operation: 'login' },
            contexts: {
              auth: { username },
            },
          });
        }

        return {
          token: null,
          error: errorMessage,
        };
      }
    }
  );
};

export const getPreviousHistory = async (
  token: string,
  activePage: number,
  signal?: AbortSignal | null
): Promise<IPrevNotes> => {
  return Sentry.startSpan(
    {
      op: 'http.client',
      name: 'GET /v1/sb_note/previous_notes',
    },
    async span => {
      const { base_id } = parseJwt(token);

      span.setAttribute('page', activePage);

      const requestOptions: RequestInit = withAuth(
        { method: 'GET' },
        token,
        signal ?? null
      );

      try {
        const data: IPrevNotes = await fetch(
          `${VITE_DEV_ENDPOINT_URL}/v1/sb_note/previous_notes?baseid=${base_id}&page=${activePage}`,
          requestOptions
        )
          .then(resp => resp.json())
          .then(data => {
            span.setAttribute('notes_count', data?.notes?.length || 0);
            return data;
          });

        return data;
      } catch (reason: unknown) {
        if (isAbortError(reason)) {
          span.setStatus({ code: 1, message: 'Aborted' });
          throw reason;
        }

        Sentry.captureException(reason, {
          level: 'error',
          tags: { operation: 'get_previous_history' },
          contexts: {
            request: { page: activePage },
          },
        });

        throw reason;
      }
    }
  );
};
